import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, FileText, GripVertical, Loader2, Eye, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * CL-SF-EXPORT-001 — the "Salesforce Extra Fields" preset of the Export page.
 *
 * A file for a Salesforce team's mass upload: 23 fixed headers whose ORDER can be dragged, the
 * company constants, one bulk toggle for a generated info@ debtor email, a preview, and CSV/XLSX
 * download. Everything is edited in a local draft that is sent with each preview/download, so a
 * member customises THAT download only; only a company admin can save the draft as the company
 * default (the backend enforces it as well).
 */

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api`;
const CONFIG_URL = `${API_URL}/export-presets/salesforce-extra-fields`;

// UI hints only — never sent, never a default (D3).
const HINTS = {
  LastName: 'e.g. Support',
  Status: 'e.g. New',
  Company: 'e.g. your Salesforce company label',
  Owner_ID__c: 'Salesforce user Id (optional)',
  Operating_Company_Name__c: 'e.g. your Salesforce company label',
  Main_Site__c: 'e.g. 4',
  Payment_timeframe__c: 'e.g. due_in_10_days',
  LeadSource: 'e.g. Cold Calling',
  RecordTypeId: 'Record Type Id of your Salesforce org',
};

// Debtor-email statuses the backend reports as unproven (highlighted in the preview).
const UNVERIFIED_STATUSES = ['generated_unverified', 'manually_edited_unverified'];

const BINDING_LABELS = {
  legal_name_then_constant: 'Legal company name, else the constant',
  constant: 'Always the constant',
};

const copyConfig = (config) => ({
  column_order: [...config.column_order],
  constants: { ...config.constants },
  bindings: { ...config.bindings },
  generate_info_email: config.generate_info_email,
});

const readBlobJson = async (data) => {
  try {
    const text = typeof data?.text === 'function' ? await data.text() : String(data);
    return JSON.parse(text);
  } catch (error) {
    return null;
  }
};

const SalesforceExtraFieldsPanel = ({ token, searchId, canExport, isAdmin }) => {
  const [meta, setMeta] = useState(null);
  const [draft, setDraft] = useState(null);
  const [cellOverrides, setCellOverrides] = useState({});
  const [preview, setPreview] = useState(null);
  const [previewStale, setPreviewStale] = useState(false);
  const [busy, setBusy] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadConfig = useCallback(async () => {
    try {
      const response = await axios.get(CONFIG_URL, { headers: { Authorization: `Bearer ${token}` } });
      setMeta(response.data);
      setDraft(copyConfig(response.data.config));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load the Salesforce Extra Fields configuration');
    }
  }, [token]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    setPreview(null);
    setCellOverrides({});
  }, [searchId]);

  const changeDraft = (next) => {
    setDraft(next);
    setPreviewStale(true);
  };

  const setConstant = (header, value) => changeDraft({ ...draft, constants: { ...draft.constants, [header]: value } });
  const setBinding = (header, value) => changeDraft({ ...draft, bindings: { ...draft.bindings, [header]: value } });

  const requestBody = () => ({ overrides: draft, cell_overrides: cellOverrides });

  const runPreview = async () => {
    if (!searchId || !canExport) return;
    setBusy('preview');
    try {
      const response = await axios.post(`${API_URL}/exports/${searchId}/salesforce-extra-fields/preview`, requestBody(), auth);
      setPreview(response.data);
      setPreviewStale(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Preview failed');
    } finally {
      setBusy('');
    }
  };

  const download = async (format) => {
    if (!searchId || !canExport) return;
    setBusy(format);
    try {
      const response = await axios.post(
        `${API_URL}/exports/${searchId}/salesforce-extra-fields/download?format=${format}`,
        requestBody(),
        { ...auth, responseType: 'blob' },
      );
      const contentDisposition = response.headers?.['content-disposition'];
      let filename = `ciaolead_salesforce_extra_fields.${format}`;
      const match = contentDisposition?.match(/filename=(.+)/);
      if (match) filename = match[1];
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported Salesforce Extra Fields as ${format.toUpperCase()}`);
    } catch (error) {
      const body = await readBlobJson(error.response?.data);
      const detail = body?.detail;
      if (detail?.issues) {
        setPreview((current) => (current ? { ...current, issues: detail.issues, csv_blocked: true } : current));
      }
      toast.error(detail?.message || (typeof detail === 'string' ? detail : 'Export failed'));
    } finally {
      setBusy('');
    }
  };

  const saveCompanyDefault = async () => {
    setBusy('save');
    try {
      const response = await axios.put(CONFIG_URL, draft, auth);
      setMeta(response.data);
      setDraft(copyConfig(response.data.config));
      toast.success('Saved as the company default');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save the configuration');
    } finally {
      setBusy('');
    }
  };

  const resetCompanyDefault = async () => {
    setBusy('reset');
    try {
      const response = await axios.post(`${CONFIG_URL}/reset`, {}, auth);
      setMeta(response.data);
      changeDraft(copyConfig(response.data.config));
      toast.success('Company default reset');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset the configuration');
    } finally {
      setBusy('');
    }
  };

  const resetDraft = () => changeDraft(copyConfig(meta.default_config));

  const setCell = (leadId, header, value) => {
    setCellOverrides((current) => ({ ...current, [leadId]: { ...(current[leadId] || {}), [header]: value } }));
    setPreviewStale(true);
  };

  // Drag & drop: reorders the 23 headers, never renames or removes one.
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      const order = [...draft.column_order];
      const [moved] = order.splice(draggedIndex, 1);
      order.splice(dropIndex, 0, moved);
      changeDraft({ ...draft, column_order: order });
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  if (!meta || !draft) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
      </div>
    );
  }

  const csvBlocked = Boolean(preview?.csv_blocked) && !previewStale;
  const xlsxBlocked = Boolean(preview?.xlsx_blocked) && !previewStale;
  const emailStatusIndex = preview ? preview.headers.indexOf('debitor_email__c') : -1;

  return (
    <div className="space-y-5" data-testid="sf-extra-fields-panel">
      <p className="text-xs text-slate-400">
        A file for your Salesforce team&apos;s bulk upload. All 23 columns are always included; drag to change their order.
        {!isAdmin && ' Your changes apply to this download only.'}
      </p>

      {/* Column order */}
      <div>
        <p className="text-sm font-medium text-slate-200 mb-2">Columns (23)</p>
        <div className="flex flex-wrap gap-2" data-testid="sf-column-chips">
          {draft.column_order.map((header, index) => (
            <div
              key={header}
              draggable
              onDragStart={(e) => {
                setDraggedIndex(index);
                if (e.dataTransfer) {
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', String(index));
                }
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (draggedIndex !== null && draggedIndex !== index) setDragOverIndex(index);
              }}
              onDragLeave={() => setDragOverIndex(null)}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={() => {
                setDraggedIndex(null);
                setDragOverIndex(null);
              }}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-white/10 text-slate-200
                cursor-grab active:cursor-grabbing border border-transparent transition-all
                ${draggedIndex === index ? 'opacity-50' : ''} ${dragOverIndex === index ? 'border-blue-400 bg-blue-500/10' : ''}`}
              data-testid={`sf-column-chip-${header}`}
            >
              <GripVertical className="h-3 w-3 text-slate-400" />
              <span>{header}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Company constants */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {meta.constant_headers.map((header) => (
          <label key={header} className="block text-xs text-slate-300">
            <span className="font-mono">{header}</span>
            <input
              type="text"
              value={draft.constants[header] || ''}
              placeholder={HINTS[header] || ''}
              onChange={(e) => setConstant(header, e.target.value)}
              maxLength={255}
              className="mt-1 w-full rounded-md border border-white/10 bg-white/5 px-2 py-1.5 text-sm text-white placeholder:text-slate-500"
              data-testid={`sf-constant-${header}`}
            />
          </label>
        ))}
        {meta.bindable_headers.map((header) => (
          <label key={`binding-${header}`} className="block text-xs text-slate-300">
            <span className="font-mono">{header}</span> value
            <select
              value={draft.bindings[header]}
              onChange={(e) => setBinding(header, e.target.value)}
              className="mt-1 w-full rounded-md border border-white/10 bg-slate-900 px-2 py-1.5 text-sm text-white"
              data-testid={`sf-binding-${header}`}
            >
              {Object.entries(BINDING_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>
        ))}
      </div>

      <label className="flex items-start gap-2 text-sm text-slate-200">
        <input
          type="checkbox"
          checked={draft.generate_info_email}
          onChange={(e) => changeDraft({ ...draft, generate_info_email: e.target.checked })}
          className="mt-1"
          data-testid="sf-generate-email"
        />
        <span>
          Auto-generate info@domain for debtor email
          <span className="block text-xs text-slate-400">
            Only when no info@ was found on the merchant&apos;s own website and the domain is certainly the merchant&apos;s.
            Generated addresses are not verified and are never saved.
          </span>
        </span>
      </label>

      {/* Configuration actions */}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={resetDraft} data-testid="sf-reset-default">
          Reset to Default
        </Button>
        {isAdmin && (
          <>
            <Button size="sm" onClick={saveCompanyDefault} disabled={busy === 'save'} data-testid="sf-save-company">
              Save as company default
            </Button>
            <Button variant="outline" size="sm" onClick={resetCompanyDefault} disabled={busy === 'reset'} data-testid="sf-reset-company">
              Reset company default
            </Button>
          </>
        )}
      </div>

      {searchId && !canExport && (
        <p className="text-sm text-amber-300" data-testid="sf-no-approved">
          No approved leads to export. Approve some leads first from the Search page.
        </p>
      )}

      {/* Preview + download */}
      {searchId && canExport && (
        <div className="space-y-3 border-t border-white/10 pt-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="outline" onClick={runPreview} disabled={busy !== ''} className="flex-1" data-testid="sf-preview-btn">
              {busy === 'preview' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />}
              {preview && previewStale ? 'Refresh preview' : 'Preview'}
            </Button>
            <Button
              onClick={() => download('csv')}
              disabled={busy !== '' || csvBlocked}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              data-testid="sf-download-csv"
            >
              {busy === 'csv' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
              Download CSV
            </Button>
            <Button
              onClick={() => download('xlsx')}
              disabled={busy !== '' || xlsxBlocked}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              data-testid="sf-download-xlsx"
            >
              {busy === 'xlsx' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />}
              Download Excel
            </Button>
          </div>

          {preview && (
            <div className="space-y-3" data-testid="sf-preview">
              {previewStale && (
                <p className="text-xs text-amber-300" data-testid="sf-preview-stale">
                  Settings changed since this preview - refresh it to check the file again.
                </p>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-slate-400">Rows</p>
                  <p className="font-semibold text-white" data-testid="sf-total-rows">{preview.total_rows}</p>
                </div>
                {Object.entries(preview.email_summary).map(([label, count]) => (
                  <div key={label}>
                    <p className="text-slate-400">Debtor email: {label}</p>
                    <p className="font-semibold text-white" data-testid={`sf-email-summary-${label}`}>{count}</p>
                  </div>
                ))}
              </div>

              <p className="text-xs text-slate-400" data-testid="sf-missing-counts">
                Empty cells:{' '}
                {preview.headers
                  .filter((header) => preview.missing_counts[header] > 0)
                  .map((header) => `${header} ${preview.missing_counts[header]}`)
                  .join(' · ') || 'none'}
              </p>

              {preview.issues.length > 0 && (
                <div className="border border-amber-500/40 bg-amber-500/10 rounded-lg p-3 space-y-2" data-testid="sf-issues">
                  <div className="flex items-center gap-2 text-sm text-amber-300">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>
                      {preview.issues.length} cell(s) could be read as a spreadsheet formula or hold a control character.
                      The CSV download stays blocked until you edit them; nothing is changed automatically.
                    </span>
                  </div>
                  {preview.issues.map((issue) => {
                    const key = `${issue.lead_id}-${issue.header}`;
                    const current = cellOverrides[issue.lead_id]?.[issue.header];
                    return (
                      <label key={key} className="block text-xs text-slate-300">
                        <span className="font-mono">{issue.header}</span> ({issue.message})
                        <input
                          type="text"
                          value={current ?? issue.value}
                          onChange={(e) => setCell(issue.lead_id, issue.header, e.target.value)}
                          className="mt-1 w-full rounded-md border border-white/10 bg-white/5 px-2 py-1 text-sm text-white"
                          data-testid={`sf-issue-${key}`}
                        />
                      </label>
                    );
                  })}
                </div>
              )}

              <div className="overflow-x-auto border border-white/10 rounded-lg" data-testid="sf-preview-table">
                <table className="min-w-full text-xs text-slate-200">
                  <thead>
                    <tr>
                      <th className="px-2 py-1 text-left text-slate-400 whitespace-nowrap">Debtor email status (preview only)</th>
                      {preview.headers.map((header) => (
                        <th key={header} className="px-2 py-1 text-left font-mono whitespace-nowrap">{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row) => (
                      <tr key={row.lead_id} className="border-t border-white/10">
                        <td className="px-2 py-1 whitespace-nowrap" data-testid={`sf-email-status-${row.lead_id}`}>
                          {row.email_status_label}
                        </td>
                        {row.values.map((value, index) => (
                          <td
                            key={preview.headers[index]}
                            className={`px-2 py-1 whitespace-nowrap ${index === emailStatusIndex && UNVERIFIED_STATUSES.includes(row.email_status) ? 'text-amber-300' : ''}`}
                          >
                            {value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.total_rows > preview.rows.length && (
                <p className="text-xs text-slate-400">
                  Showing the first {preview.rows.length} of {preview.total_rows} rows. The download contains all rows.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-slate-500 flex items-center gap-1">
        <Download className="h-3 w-3" />
        The file still has to be tested by your Salesforce team in their own org before a real import.
      </p>
    </div>
  );
};

export default SalesforceExtraFieldsPanel;
